import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { colors, typography } from '../../../styles/theme';
import { FILTER_ACCENT_14 } from '../FilterPanel/FilterPanel.styles';

/**
 * Estado 1 del buscador NL: foco sin texto.
 * Recientes (historial del usuario) + relleno de Sugeridas (pool distinto del placeholder).
 * Sparkle solo en el input; acá lupa / reloj. Recientes se pueden quitar de a una.
 *
 * @param {{
 *   suggestions?: string[],
 *   recentQueries?: string[],
 *   onSelectQuery?: (query: string) => void,
 *   onForgetQuery?: (query: string) => void,
 * }} props
 */
function SearchIdleSuggestions({
  suggestions = [],
  recentQueries = [],
  onSelectQuery,
  onForgetQuery,
}) {
  const hasRecents = recentQueries.length > 0;
  const hasSuggestions = suggestions.length > 0;

  if (!hasRecents && !hasSuggestions) return null;

  return (
    <div style={styles.wrap}>
      {hasRecents && (
        <QueryGroup
          label="Recientes"
          queries={recentQueries}
          Icon={HistoryIcon}
          onSelectQuery={onSelectQuery}
          onForgetQuery={onForgetQuery}
        />
      )}
      {hasSuggestions && (
        <QueryGroup
          label="Sugeridas"
          queries={suggestions}
          Icon={SearchIcon}
          onSelectQuery={onSelectQuery}
        />
      )}
    </div>
  );
}

function QueryGroup({ label, queries, Icon, onSelectQuery, onForgetQuery }) {
  return (
    <div style={styles.group} role="group" aria-label={label}>
      <div style={styles.label}>{label}</div>
      <div style={styles.chips}>
        {queries.map((query) => (
          <SuggestionChip
            key={`${label}-${query}`}
            query={query}
            Icon={Icon}
            onSelect={onSelectQuery}
            onForget={onForgetQuery}
          />
        ))}
      </div>
    </div>
  );
}

function SuggestionChip({ query, Icon, onSelect, onForget }) {
  const [hovered, setHovered] = useState(false);
  const [removeHovered, setRemoveHovered] = useState(false);
  const canForget = typeof onForget === 'function';

  return (
    <div
      style={{
        ...styles.chip,
        ...(hovered ? styles.chipHover : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        style={styles.chipMain}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect?.(query)}
      >
        <Icon style={styles.chipIcon} aria-hidden />
        <span style={styles.chipLabel}>{query}</span>
      </button>
      {canForget && (
        <button
          type="button"
          aria-label={`Quitar “${query}” de recientes`}
          title="Quitar de recientes"
          style={{
            ...styles.chipRemove,
            ...(removeHovered ? styles.chipRemoveHover : {}),
          }}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setRemoveHovered(true)}
          onMouseLeave={() => setRemoveHovered(false)}
          onClick={(e) => {
            e.stopPropagation();
            onForget(query);
          }}
        >
          <CloseIcon style={styles.chipRemoveIcon} aria-hidden />
        </button>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 14px 14px',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
  },
  label: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.tight,
    color: colors.textSecondary,
  },
  chips: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%',
    padding: '2px 4px 2px 10px',
    boxSizing: 'border-box',
    color: colors.textPanelDescription,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    transition: 'background-color 0.12s ease',
  },
  chipHover: {
    backgroundColor: FILTER_ACCENT_14,
    border: `1px solid ${colors.border}`,
  },
  chipMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
    margin: 0,
    padding: '6px 0',
    appearance: 'none',
    WebkitAppearance: 'none',
    color: colors.textPanelDescription,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: typography.fontFamily.primary,
  },
  chipIcon: {
    fontSize: '16px',
    width: '16px',
    height: '16px',
    color: colors.textSecondary,
    flexShrink: 0,
  },
  chipLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.regular,
    lineHeight: typography.lineHeight.normal,
    color: colors.textPanelDescription,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '28px',
    height: '28px',
    margin: 0,
    padding: 0,
    appearance: 'none',
    WebkitAppearance: 'none',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    cursor: 'pointer',
  },
  chipRemoveHover: {
    color: colors.textPanelDescription,
    backgroundColor: FILTER_ACCENT_14,
  },
  chipRemoveIcon: {
    fontSize: '16px',
    width: '16px',
    height: '16px',
  },
};

export default SearchIdleSuggestions;
